// Angular
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

// Libraries
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarRef, MatSnackBarVerticalPosition, SimpleSnackBar } from '@angular/material/snack-bar';
import { Observable, of, takeUntil } from 'rxjs';
import { Select, Store } from '@ngxs/store';

// Models
import { AppCleanup } from '../models';
import { ToasterModel } from '../models';

// Components
import { CustomSnackbarComponent } from '../components';

// Services
import { AudioService } from './audio.service';

// Store
import { StorageState } from '../state/storage.state';
import { StorageActions } from '../state/storage.action';

// Enums
import { StatusType } from '../enums';

// Helpers
import { UtilityHelper } from '../helpers';

@Injectable()
export class CoreService extends AppCleanup {
	@Select(StorageState.toastHistory) toastHistory$: Observable<ToasterModel[]>;
	toastMessages: ToasterModel[] = [];
	readonly timeDifferenceInMinutes: number = 15;

	constructor(
		private snackBar: MatSnackBar,
		private audioService: AudioService,
		private store: Store
	) {
		super();

		this.toastHistory$.pipe(takeUntil(this.destroy)).subscribe((toastHistory) => {
			if (toastHistory) {
				this.toastMessages = UtilityHelper.deepCopy(toastHistory);
			}
		});
	}

	toast(model: ToastModel) {
		model.type = model.type ?? StatusType.INFO;
		model.horizontalPosition = model.horizontalPosition ?? 'right';
		model.verticalPosition = model.verticalPosition ?? 'top';
		model.duration = model.duration ?? 8000;
		model.infinite = model.infinite ?? false;
		model.pushToNotifications = model.pushToNotifications ?? true;

		let panelClass = this.getClassFromType(model.type);

		if (model.infinite) {
			model.duration = undefined;
		}

		this.snackBar.openFromComponent(CustomSnackbarComponent, {
			data: {
				message: model.message,
				icon: 'close',
				panelClass: panelClass,
			},
			horizontalPosition: model.horizontalPosition,
			verticalPosition: model.verticalPosition,
			panelClass: panelClass,
			duration: model.duration,
		});

		// this.audioService.playNotificationAudio();

		if (model.pushToNotifications) {
			// Pushes the toast into the store so that it stay under notifications
			this.addToastToHistory({ message: model.message, type: model.type, date: new Date(), countMessage: true, remainingMinutes: 0 });
		}
	}

	toastWithAction(message: string, action: string, type: StatusType = StatusType.INFO, horizontalPosition: MatSnackBarHorizontalPosition = 'right', verticalPosition: MatSnackBarVerticalPosition = 'top', duration = 15000): MatSnackBarRef<SimpleSnackBar> {
		const snackBarRef = this.snackBar.open(message, action, {
			horizontalPosition: horizontalPosition,
			verticalPosition: verticalPosition,
			panelClass: type,
			duration: duration,
		});

		this.addToastToHistory({ message: message, type: type, date: new Date(), countMessage: true, remainingMinutes: 0 });

		return snackBarRef;
	}

	handleError<T>(message: string = null): any {
		return (response: HttpErrorResponse): Observable<T> => {
			// TODO: send the error to remote logging infrastructure
			// console.error(error);

			if (message !== null) {
				this.toast({ message: message, type: StatusType.DANGER });
			}

			// TODO: better job of transforming error for user consumption
			// Let the app keep running by returning an empty result.
			return of(response.error as T);
		};
	}

	// TODO - Flag for removal, not in use... confirm w/ Isaac.
	/*handleErrorResponse<T>(defaultMessage: string = 'Your request failed') {
        return (response: HttpErrorResponse): Observable<T> => {
            let message: string = defaultMessage;
            if (response.error) {
                const errorResponse: ErrorResponse = response.error;

                const messages: string[] = [];
                if (errorResponse.message) {
                    messages.push(errorResponse.message);
                }
                if (errorResponse.details && errorResponse.details.length > 0) {
                    errorResponse.details.forEach(err => messages.push(err.message));
                }
                if (messages.length > 0) {
                    message = messages.join('\n');
                }
            }
            if (message) {
                this.toast({ message, type: StatusType.DANGER });
            }
            return of(<T>{});
        }
    }*/

	addToastToHistory(toast: ToasterModel) {
		this.toastMessages.push(toast);
		this.filteredToastMessageHistory();
	}

	filteredToastMessageHistory(): any {
		const now = new Date();
		const nowWithTimeDiff = now.setMinutes(now.getMinutes() - this.timeDifferenceInMinutes);

		this.toastMessages = this.toastMessages.filter((item: ToasterModel) => {
			return new Date(item.date) >= new Date(nowWithTimeDiff);
		});

		this.toastMessages.sort((a, b) => {
			return new Date(a.date).getTime() - new Date(b.date).getTime();
		});

		this.store.dispatch(new StorageActions.SetToastHistory(this.toastMessages));
		return this.toastMessages;
	}

	getClassFromType(type: string) {
		let panelClass: string;

		switch (type) {
			case StatusType.SUCCESS: {
				panelClass = SnackbarClassType.SUCCESS;
				break;
			}
			case StatusType.DANGER: {
				panelClass = SnackbarClassType.DANGER;
				break;
			}
			case StatusType.WARNING: {
				panelClass = SnackbarClassType.WARNING;
				break;
			}
			case StatusType.INFO: {
				panelClass = SnackbarClassType.INFO;
				break;
			}

			default: {
				panelClass = SnackbarClassType.INFO;
				break;
			}
		}

		return panelClass;
	}
}

export enum SnackbarClassType {
	SUCCESS = 'app-toast-success',
	DANGER = 'app-toast-danger',
	WARNING = 'app-toast-warning',
	INFO = 'app-toast-info',
}

export class ToastModel {
	message: string;
	type?: StatusType;
	horizontalPosition?: MatSnackBarHorizontalPosition;
	verticalPosition?: MatSnackBarVerticalPosition;
	duration?: number;
	infinite?: boolean;
	pushToNotifications?: boolean;
}
